<script setup lang="ts">
import DialogCross from "@/components/shared/DialogCross.vue";
import DialogIcon from "@/components/shared/DialogIcon.vue";
import { useCompactScopeBoundary } from "@/composables/shared/useCompactScope";
import { computed, useSlots, type CSSProperties, type StyleValue } from "vue";
import { useDisplay } from "vuetify";

type DialogSectionName = "prepend" | "title" | "subtitle" | "text" | "body" | "actions";

type DialogClassValue = string | Record<string, boolean> | DialogClassValue[];

type DialogSectionConfig = {
  class?: DialogClassValue;
  style?: StyleValue;
};

type BaseDialogProps = {
  openWithParent?: boolean;
  showClose?: boolean;
  maxWidth?: string | number;
  maxHeight?: string | number;
  contentClass?: string;
  persistent?: boolean;
  /**
   * Route Escape and outside-click through `@close` instead of letting Vuetify close the dialog
   * itself. Use when the host runs an unsaved-changes guard before allowing the close — unlike
   * `persistent` (which only plays a "can't close" pulse and swallows the gesture), the dismissal
   * still reaches the host, which decides whether to honour it.
   */
  guardedClose?: boolean;
  rounded?: string;
  cardClass?: DialogClassValue;
  cardStyle?: StyleValue;
  dialogProps?: Record<string, string | number | boolean | undefined>;
  cardProps?: Record<string, string | number | boolean | undefined>;
  sectionProps?: Partial<Record<DialogSectionName, DialogSectionConfig>>;
  /**
   * `centered` (default) is the app's usual dialog: icon bubble, centred title, padded white card.
   * `panel` is the document layout — a white title strip above a tinted body of white cards — for
   * dialogs that present a record rather than ask a question. It suppresses the icon and the card's
   * own padding, so the sections carry their own.
   */
  variant?: "centered" | "panel";
  showIcon?: boolean;
  icon?: string;
  iconColor?: string;
  iconSize?: number;
  iconBackgroundColor?: string;
  loading?: boolean;
  fullscreenOnMobile?: boolean;
  /**
   * Blur what is behind the dialog so it cannot be read, on top of the usual dimming. Pass
   * `true` for the default radius or a CSS length (`"32px"`, `"2rem"`) for a stronger/weaker
   * one. The default is deliberately heavy — the point is illegibility, not a frosted-glass
   * look; see `.dialog-blurred-overlay` in `style.css`.
   */
  blurOverlay?: boolean | string;
};

type ResolvedBaseDialogProps = {
  openWithParent: boolean;
  showClose: boolean;
  maxWidth: string | number;
  maxHeight: string | number;
  contentClass: string;
  persistent: boolean;
  guardedClose: boolean;
  rounded: string;
  cardClass: DialogClassValue;
  cardStyle: StyleValue;
  dialogProps: Record<string, string | number | boolean | undefined>;
  cardProps: Record<string, string | number | boolean | undefined>;
  sectionProps: Partial<Record<DialogSectionName, DialogSectionConfig>>;
  showIcon: boolean;
  icon: string;
  iconColor?: string;
  iconSize: number;
  iconBackgroundColor: string;
  fullscreenOnMobile: boolean;
  blurOverlay: boolean | string;
};

const dialogOpen = defineModel<boolean>({ default: false });

const props = withDefaults(defineProps<BaseDialogProps>(), {
  openWithParent: false,
  showClose: true,
  maxWidth: "560px",
  maxHeight: "1000px",
  contentClass: "",
  persistent: false,
  guardedClose: false,
  rounded: "xl",
  cardClass: "tw:relative tw:p-6",
  cardStyle: () => ({}),
  dialogProps: () => ({}),
  cardProps: () => ({}),
  sectionProps: () => ({}),
  variant: "centered",
  showIcon: true,
  icon: "mdi-check",
  iconSize: 40,
  iconBackgroundColor: "rgb(var(--v-theme-secondary))",
  fullscreenOnMobile: true,
  blurOverlay: false,
});

useCompactScopeBoundary();

const emit = defineEmits<{
  close: [];
}>();

const resolvedProps = computed<ResolvedBaseDialogProps>(() => ({
  openWithParent: props.openWithParent,
  showClose: props.showClose,
  maxWidth: props.maxWidth,
  maxHeight: props.maxHeight,
  contentClass: props.contentClass,
  persistent: props.persistent,
  guardedClose: props.guardedClose,
  rounded: props.rounded,
  cardClass: props.cardClass,
  cardStyle: props.cardStyle,
  dialogProps: props.dialogProps,
  cardProps: props.cardProps,
  sectionProps: props.sectionProps,
  showIcon: props.showIcon,
  icon: props.icon,
  iconColor: props.iconColor,
  iconSize: props.iconSize,
  iconBackgroundColor: props.iconBackgroundColor,
  fullscreenOnMobile: props.fullscreenOnMobile,
  blurOverlay: props.blurOverlay,
}));

const slots = useSlots();

const isPanel = computed(() => props.variant === "panel");

/**
 * A panel's sections paint their own backgrounds edge to edge, and the card cannot clip them —
 * `overflow` stays visible so the icon can straddle the top edge. So the outermost rendered
 * sections carry the card's corner radius themselves.
 */
// A plain function, not a computed: whether a slot renders anything is not a reactive dependency,
// so a cached value goes stale the moment a slot's own `v-if` flips (e.g. once data loads).
const renderedPanelSections = (): DialogSectionName[] =>
  ([...headerSections, "body", "actions"] as DialogSectionName[]).filter((section) =>
    shouldRenderSection(section)
  );

const panelCornerClass = (section: DialogSectionName): string => {
  if (!isPanel.value) return "";
  const sections = renderedPanelSections();
  return [
    sections[0] === section ? "tw:rounded-t-3xl" : "",
    sections.at(-1) === section ? "tw:rounded-b-3xl" : "",
  ]
    .filter(Boolean)
    .join(" ");
};

const shouldShowIcon = computed(() => {
  return resolvedProps.value.showIcon && !isMobileFullscreen.value;
});

const handleClose = () => {
  emit("close");
  dialogOpen.value = false;
};

/**
 * Every dismissal gesture (cross, Escape, outside click) lands here.
 *
 * In `guardedClose` mode the host owns the close decision, so only tell it a dismissal was
 * requested — never flip `dialogOpen` here. It closes the dialog itself once its guard clears.
 * Flipping it anyway would tear the dialog down *while* the guard's discard prompt is mounting,
 * so the prompt flashes and its close promise never settles.
 */
const requestClose = () => {
  if (resolvedProps.value.guardedClose) {
    emit("close");
    return;
  }

  handleClose();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key !== "Escape" || !resolvedProps.value.guardedClose) return;

  // Vuetify still emits `keydown` for a persistent overlay (right before it plays the
  // can't-close pulse), which is the only hook we get for Escape.
  requestClose();
};

const headerSections: DialogSectionName[] = ["prepend", "title", "subtitle", "text"];

const defaultSectionClass = computed<Partial<Record<DialogSectionName, DialogClassValue>>>(() => {
  if (isPanel.value) {
    return {
      prepend: "tw:bg-surface tw:px-6! tw:pt-5!",
      title: [
        "tw:bg-surface tw:px-6! tw:text-xl tw:font-extrabold tw:text-high-emphasis",
        shouldShowIcon.value ? "tw:pt-10!" : "tw:pt-5!",
      ],
      subtitle: "tw:bg-surface tw:px-6! tw:pb-5! tw:mt-1! tw:text-[13px] tw:text-medium-emphasis",
      text: "tw:bg-surface tw:px-6! tw:pb-5! tw:text-sm tw:text-medium-emphasis",
      body: "tw:bg-background tw:p-5!",
      actions: "tw:bg-background tw:px-5! tw:pb-5!",
    };
  }

  return {
    prepend: "tw:mb-4",
    title: [
      "tw:text-xl tw:font-bold tw:leading-7 tw:text-high-emphasis tw:text-center",
      shouldShowIcon.value ? "tw:mt-6" : "tw:mt-0",
    ],
    subtitle: "tw:mt-1 tw:text-sm tw:text-medium-emphasis tw:text-center",
    text: "tw:mt-3 tw:text-sm tw:text-medium-emphasis",
    body: "tw:mt-5 tw:px-2",
    actions: "tw:mt-6",
  };
});

const toCssSize = (value: string | number) => {
  if (typeof value === "number") {
    return `${value}px`;
  }

  const trimmedValue = value.trim();

  if (/^\d+(\.\d+)?$/.test(trimmedValue)) {
    return `${trimmedValue}px`;
  }

  return trimmedValue;
};

const normalizedMaxHeight = computed(() => toCssSize(resolvedProps.value.maxHeight));

/**
 * The dialog icon is a 72px circle centered on the card's top edge, so half of it
 * (36px) hangs above the card. Reserve that much plus a little breathing room at the
 * top of the viewport, otherwise a tall dialog pins itself to the top of the browser
 * and the icon gets sliced in half.
 */
const ICON_OVERHANG_GUTTER = "3rem";

/**
 * Cap the height at the viewport minus the icon gutter (top and bottom, so the card
 * stays vertically centered). `min()` keeps an explicitly passed `maxHeight` winning
 * whenever it is the smaller of the two.
 */
const constrainedMaxHeight = computed(
  () => `min(${normalizedMaxHeight.value}, calc(100dvh - ${ICON_OVERHANG_GUTTER} * 2))`
);

const hasSectionSlot = (section: DialogSectionName) => {
  return Boolean(slots[section]);
};

const hasSectionConfig = (section: DialogSectionName) => {
  const config = resolvedProps.value.sectionProps[section];

  return Boolean(config?.class) || Boolean(config?.style);
};

const shouldRenderSection = (section: DialogSectionName) => {
  return hasSectionSlot(section) || hasSectionConfig(section);
};

const getSectionClass = (section: DialogSectionName): DialogClassValue[] => {
  return [
    defaultSectionClass.value[section] ?? "",
    panelCornerClass(section),
    resolvedProps.value.sectionProps[section]?.class ?? "",
  ];
};

const getBodyClass = (): DialogClassValue[] => {
  return [
    defaultSectionClass.value.body ?? "",
    panelCornerClass("body"),
    "tw:min-h-0 tw:flex-1 tw:overflow-y-auto",
    resolvedProps.value.sectionProps.body?.class ?? "",
  ];
};

const getActionsClass = (): DialogClassValue[] => {
  return [
    defaultSectionClass.value.actions ?? "",
    panelCornerClass("actions"),
    "tw:shrink-0",
    resolvedProps.value.sectionProps.actions?.class ?? "",
  ];
};

const getSectionStyle = (section: DialogSectionName) => {
  return resolvedProps.value.sectionProps[section]?.style;
};

const { smAndDown } = useDisplay();

const isMobileFullscreen = computed(() => {
  return resolvedProps.value.fullscreenOnMobile && smAndDown.value;
});

/** Heavy on purpose: below ~16px, body text underneath is still legible. */
const DEFAULT_BLUR_RADIUS = "4px";

/**
 * Vuetify renders the scrim itself, so it never receives this component's scope id — the blur
 * lives in a global `.dialog-blurred-overlay` rule in `style.css` and we opt in per dialog by
 * putting the class on the overlay root. The radius rides along as a custom property so callers
 * can tune it without another global class per size.
 */
const overlayBlurClass = computed(() =>
  resolvedProps.value.blurOverlay ? "dialog-blurred-overlay" : ""
);

/**
 * Tell the overlay back guard it may close this dialog by pressing its cross.
 *
 * A `persistent` overlay swallows Escape and outside-click (Vuetify plays a pulse instead — see
 * VOverlay's `animateClick`), so the guard's synthetic dismissal gestures bounce off it and browser
 * back does nothing. Any dialog the user can already dismiss with the cross should behave the same
 * way for back; one with no close affordance is genuinely blocking and keeps swallowing it.
 */
const backGuardClosableClass = computed(() =>
  resolvedProps.value.showClose ? "back-guard-closable" : ""
);

const overlayBlurStyle = computed<CSSProperties>(() => {
  if (!resolvedProps.value.blurOverlay) return {};

  const radius =
    typeof resolvedProps.value.blurOverlay === "string"
      ? toCssSize(resolvedProps.value.blurOverlay)
      : DEFAULT_BLUR_RADIUS;

  return { "--dialog-overlay-blur": radius } as CSSProperties;
});

const mergedDialogProps = computed(() => ({
  maxWidth: isMobileFullscreen.value ? undefined : resolvedProps.value.maxWidth,
  maxHeight: isMobileFullscreen.value ? undefined : constrainedMaxHeight.value,
  contentClass: resolvedProps.value.contentClass,
  // `guardedClose` keeps Vuetify from closing on its own so the host's guard can run first.
  persistent: resolvedProps.value.persistent || resolvedProps.value.guardedClose,
  activator: resolvedProps.value.openWithParent ? "parent" : undefined,
  fullscreen: isMobileFullscreen.value,
  scrollable: isMobileFullscreen.value,
  ...resolvedProps.value.dialogProps,
}));

const baseCardStyle = computed<CSSProperties>(() => ({
  maxHeight: isMobileFullscreen.value ? "100dvh" : constrainedMaxHeight.value,
  height: isMobileFullscreen.value ? "100dvh" : undefined,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  // Keep the overhanging half of the icon inside the viewport even when the card is
  // short enough that the height cap above never kicks in.
  ...(shouldShowIcon.value ? { marginTop: ICON_OVERHANG_GUTTER } : {}),
  ...(isMobileFullscreen.value
    ? {
        paddingTop: "calc(1.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))",
      }
    : {}),
}));

const mergedCardProps = computed(() => ({
  rounded: isMobileFullscreen.value ? "0" : resolvedProps.value.rounded,
  class: [
    "tw:overflow-visible",
    isMobileFullscreen.value ? "tw:h-[100dvh] tw:!rounded-none dialog-mobile-fullscreen" : "",
    isPanel.value ? "tw:relative tw:p-0! tw:overflow-hidden" : resolvedProps.value.cardClass,
  ],
  style: [baseCardStyle.value, resolvedProps.value.cardStyle] as StyleValue,
  ...resolvedProps.value.cardProps,
}));
</script>

<template>
  <v-dialog
    v-model="dialogOpen"
    v-bind="mergedDialogProps"
    :class="[overlayBlurClass, backGuardClosableClass]"
    :style="overlayBlurStyle"
    @click:outside="requestClose()"
    @keydown="handleKeydown"
  >
    <template #default="{ isActive }">
      <v-card v-bind="mergedCardProps">
        <DialogIcon
          v-if="shouldShowIcon"
          :icon="resolvedProps.icon"
          :icon-color="resolvedProps.iconColor"
          :icon-size="resolvedProps.iconSize"
          :background-color="resolvedProps.iconBackgroundColor"
        />

        <DialogCross
          v-if="resolvedProps.showClose"
          :close-dialog="requestClose"
        />

        <div
          v-if="loading"
          class="tw:absolute tw:inset-0 tw:z-[1001] tw:flex tw:items-center tw:justify-center tw:rounded-xl tw:bg-white/70"
        >
          <v-progress-circular
            color="secondary"
            indeterminate
            size="48"
          />
        </div>

        <template
          v-for="section in headerSections"
          :key="section"
        >
          <div
            v-if="shouldRenderSection(section)"
            :class="getSectionClass(section)"
            :style="getSectionStyle(section)"
          >
            <slot
              :name="section"
              :is-active="isActive"
            />
          </div>
        </template>

        <div
          v-if="shouldRenderSection('body')"
          :class="getBodyClass()"
          :style="getSectionStyle('body')"
        >
          <slot
            name="body"
            :is-active="isActive"
          />
        </div>

        <div
          v-if="shouldRenderSection('actions')"
          :class="getActionsClass()"
          :style="getSectionStyle('actions')"
        >
          <slot
            name="actions"
            :is-active="isActive"
          />
        </div>
      </v-card>
    </template>
  </v-dialog>
</template>

<style scoped>
.dialog-mobile-fullscreen :deep([data-testid="close-dialog-button"]) {
  top: calc(0.75rem + env(safe-area-inset-top));
}
</style>
